/**
 * Select.jsx
 *
 * @Author: jruif
 * @Date: 2018/1/21 上午10:12
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import TagsInput from './TagInput';
import keyboardKey from '../util/keyboardKey';
import DropdownMenu from '../dropdownMenu';
// import AutoComplete from '../autoComplete';
import Menu, { MenuItem } from '../menu';

/**
 * TagSelect
 * */
class TagSelect extends React.Component {
  static displayName = 'NeatUI.TagSelect';

  static propTypes = {
    /**
     * 占位描述字段
     * */
    placeholder: PropTypes.string,
    /**
     * 预设值
     * */
    defaultValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
    ]),
    /**
     * Select的选项
     * */
    options: PropTypes.array,
    /**
     * 是否禁止
     * */
    disabled: PropTypes.bool,
    /**
     * TagSelect的ref函数，获取TagSelect的dom元素
     * */
    selectRef: PropTypes.func,
    /**
     * 自定义样式
     * */
    style: PropTypes.object,
    /**
     * Select选项
     * */
    inputProps: PropTypes.object,
    /**
     * 是否根据输入项进行筛选
     * */
    filter: PropTypes.func,
    /**
     * 文本框值变化时回调
     * */
    onChange: PropTypes.func,
    /**
     * 选择框大小，默认medium，base同medium
     * */
    size: PropTypes.oneOf(['large', 'medium', 'small', 'base']), // base === medium
    /**
     * 多选标签渲染方法
     */
    tagRender: PropTypes.bool,
    /**
     * 是否占满整行
     */
    fullWidth: PropTypes.bool,
  };

  static defaultProps = {
    prefixCls: 'ne-tag-select',
    disabled: false,
    size: 'medium',
    fullWidth: false,
    indent: 12,
    filter: (search, value) => {
      if (search === '') {
        return true;
      }
      return `${value}`.includes(search);
    },
  };

  constructor(props, context) {
    super(props, context);
    const { options, defaultValue, children } = props;
    options
      ? options.forEach(({ label, value }) => {
          this.valueToLabel[value] = label;
        })
      : React.Children.forEach(children, child => {
          if (!React.isValidElement(child)) {
            return null;
          }
          this.valueToLabel[child.props.value] = child.props.searchText || child.props.children;
        });

    let value = '';
    if (!Array.isArray(defaultValue)) {
      value = [defaultValue];
    } else {
      value = defaultValue;
    }
    this.state = {
      value,
      width: 100,
      isOpen: false,
      search: '',
    };
    this.selectRootNode = null;
  }

  componentWillReceiveProps(nextProps) {
    const { defaultValue, children, options } = this.props;
    if (children !== nextProps.children || options !== nextProps.options) {
      this.valueToLabel = {};
      nextProps.options
        ? nextProps.options.forEach(({ label, className, value, ...item }, index) => {
            this.valueToLabel[value] = label;
          })
        : React.Children.forEach(nextProps.children, (child, index) => {
            if (!React.isValidElement(child)) {
              return null;
            }
            this.valueToLabel[child.props.value] = child.props.searchText || child.props.children;
          });
    }
    if (nextProps.defaultValue !== defaultValue) {
      let value = '';
      if (!Array.isArray(nextProps.defaultValue)) {
        value = [nextProps.defaultValue];
      } else {
        value = nextProps.defaultValue;
      }
      this.setState(() => ({
        value,
        search: '',
      }));
    }
  }

  valueToLabel = {};

  getSelectWidth = isOpen => {
    let newWidth;
    const { onChange, disabled } = this.props;
    const { search, value } = this.state;
    if (!isOpen || disabled) {
      this.setState(() => ({
        isOpen: false,
      }));
      if (search && onChange) {
        if (!value.includes(search)) {
          const newValue = [...value, search];
          this.setState({
            value: newValue,
          });
          onChange && onChange(newValue, search);
        }
        this.setState({
          search: '',
        });
      }
      return;
    }

    this.setState(() => ({
      isOpen: true,
    }));
  };

  handleChange = (e, value, itemValue) => {
    const { onChange } = this.props;
    this.setState({
      search: '',
      value,
    });
    onChange && onChange(value, itemValue);
  };

  handleSearch = (e, search) => {
    this.setState({
      search,
    });
    const { onSearch } = this.props;
    onSearch && onSearch(e, search);
  };

  getTagData = (options, value) => {
    const target = this.valueToLabel[value];
    return target ? { label: target, value } : { label: value, value };
  };

  tagFilter = tag => {
    const { onChange } = this.props;
    const { value } = this.state;
    this.setState(state => ({
      value: state.value.filter(v => v !== tag),
    }));
    onChange && onChange(value.filter(v => v !== tag), tag);
  };

  selectRootNodeRef = el => {
    const { selectRef } = this.props;
    this.selectRootNode = el;
    selectRef && selectRef(el);
  };

  handleKeyDown = e => {
    const { triggerKey, disabled, onChange } = this.props;
    if (!disabled && keyboardKey.getCode(e) === triggerKey) {
      e.preventDefault();
      const { search, value } = this.state;
      if (search && onChange) {
        if (!value.includes(search)) {
          const newValue = [...value, search];
          this.setState({
            value: newValue,
          });
          onChange && onChange(newValue, search);
        }
        this.setState({
          search: '',
        });
      }
    }
  };

  render() {
    const {
      triggerKey,
      prefixCls,
      className,
      children,
      readOnly,
      defaultValue,
      placeholder,
      style,
      disabled,
      options: propOptions,
      inputProps,
      size,
      onChange,
      filter,
      onSearch,
      tagRender,
      fullWidth,
      status,
      errorIcon,
      loadingIcon,
      successIcon,
      showStatusIcon,
      prefix,
      suffix,
      id,
      ...other
    } = this.props;
    const { isOpen, value, search } = this.state;
    const options = propOptions
      ? propOptions.map(({ label, ...item }, index) => (
          <MenuItem
            value={value}
            className={classNames(`${prefixCls}-menu-item`, className)}
            key={value || index}
            {...item}
            role="option">
            {label}
          </MenuItem>
        ))
      : React.Children.map(children, (child, index) => {
          if (!React.isValidElement(child)) {
            return null;
          }
          return React.cloneElement(child, {
            key: child.props.value || index,
            role: 'option',
          });
        });
    const filtered = options.filter(option =>
      filter(search, option.props.searchText || option.props.children, option.props.value),
    );
    return (
      <div className={classNames(prefixCls, className)} ref={this.selectRootNodeRef}>
        <DropdownMenu
          autoWidth
          anchorEl={
            <TagsInput
              id={id}
              suffix={suffix}
              status={status}
              errorIcon={errorIcon}
              loadingIcon={loadingIcon}
              successIcon={successIcon}
              showStatusIcon={showStatusIcon}
              aria-haspopup="true"
              role="button"
              onTagDelete={this.tagFilter}
              onChange={this.handleSearch}
              searchText={search}
              readOnly={readOnly}
              onKeyDown={this.handleKeyDown}
              tags={value
                .filter(tag => this.getTagData(options, tag))
                .map(tag => this.getTagData(options, tag))}
              placeholder={placeholder}
              inputProps={inputProps}
              className={classNames(`${prefixCls}-input`, {
                [`${prefixCls}-open`]: isOpen && !disabled,
                [`${prefixCls}-disabled`]: disabled,
                [`${prefixCls}-multiple`]: true,
              })}
              tagRender={tagRender}
              style={style}
              disabled={disabled}
              searchable
              size={size}/>
          }
          trigger="focus"
          disabled={disabled}
          onOpenChange={this.getSelectWidth}
          defaultIsOpen={isOpen}
          noClose>
          <Menu
            {...other}
            indentBase={0}
            selectable
            multiple
            onChange={this.handleChange}
            className={classNames(`${prefixCls}-menu`)}
            value={value}
            role="listbox"
            size={size}>
            {filtered}
          </Menu>
        </DropdownMenu>
      </div>
    );
  }
}

export default TagSelect;



// WEBPACK FOOTER //
// ./src/tagSelect/TagSelect.jsx